import React, { useState, useEffect } from "react";
import { FaRobot, FaPlus, FaTrash, FaEdit, FaSave, FaMicrophone, FaGlobe, FaMagic } from "react-icons/fa";
import { apiGet, apiPut, apiPost, apiDelete } from "../../utils/api";

/**
 * Inline "Test" button for one saved key. Calls POST /api/settings/ai/test with
 * a target so each card verifies its own credential, and shows the result.
 */
function TestKeyButton({ target, testState, onTest }) {
  const st = testState[target] || {};
  const r = st.result;
  const entry = r?.chat || r?.voice;
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        className="btn-sm border border-surface-border text-ink-secondary hover:bg-surface-bg"
        onClick={() => onTest(target)}
        disabled={st.loading}
      >
        {st.loading ? "Testing..." : "Test"}
      </button>
      {r && (
        <span className={`text-2xs ${r.ok ? "text-emerald-600" : "text-red-600"}`}>
          {entry?.message || (r.ok ? "Connection OK." : "Connection failed.")}
        </span>
      )}
    </span>
  );
}

export default function AISettingsTab() {
  const [hasKey, setHasKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState("");
  const [newKey, setNewKey] = useState("");
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [saving, setSaving] = useState(false);

  // ElevenLabs state
  const [hasElevenLabsKey, setHasElevenLabsKey] = useState(false);
  const [maskedElevenLabsKey, setMaskedElevenLabsKey] = useState("");
  const [newElevenLabsKey, setNewElevenLabsKey] = useState("");
  const [showElevenLabsInput, setShowElevenLabsInput] = useState(false);
  const [savingElevenLabs, setSavingElevenLabs] = useState(false);

  // OpenRouter state
  const [hasOpenRouterKey, setHasOpenRouterKey] = useState(false);
  const [maskedOpenRouterKey, setMaskedOpenRouterKey] = useState("");
  const [newOpenRouterKey, setNewOpenRouterKey] = useState("");
  const [showOpenRouterInput, setShowOpenRouterInput] = useState(false);
  const [savingOpenRouter, setSavingOpenRouter] = useState(false);

  // Provider selection
  const [aiProvider, setAiProvider] = useState("openai");
  const [aiModel, setAiModel] = useState("");
  const [savingProvider, setSavingProvider] = useState(false);

  // Live connection check
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testState, setTestState] = useState({});

  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [showKbForm, setShowKbForm] = useState(false);
  const [editingKb, setEditingKb] = useState(null);
  const [kbForm, setKbForm] = useState({ name: "", type: "text", content: "" });
  const [kbSaving, setKbSaving] = useState(false);

  // Website → knowledge base builder
  const [siteUrl, setSiteUrl] = useState("");
  const [siteName, setSiteName] = useState("");
  const [siteMaxPages, setSiteMaxPages] = useState(30);
  const [siteScanning, setSiteScanning] = useState(false);
  const [siteError, setSiteError] = useState("");
  const [sitePreview, setSitePreview] = useState(null);
  const [siteSaving, setSiteSaving] = useState(false);

  useEffect(() => {
    loadSettings();
    loadKnowledgeBases();
  }, []);

  const loadSettings = async () => {
    const res = await apiGet("/api/settings/ai");
    if (res.success) {
      setHasKey(res.hasKey);
      setMaskedKey(res.maskedKey);
      setHasElevenLabsKey(res.hasElevenLabsKey);
      setMaskedElevenLabsKey(res.maskedElevenLabsKey);
      setHasOpenRouterKey(res.hasOpenRouterKey);
      setMaskedOpenRouterKey(res.maskedOpenRouterKey);
      setAiProvider(res.aiProvider || "openai");
      setAiModel(res.aiModel || "");
    }
  };

  const loadKnowledgeBases = async () => {
    const res = await apiGet("/api/settings/ai/knowledge");
    if (res.success) setKnowledgeBases(res.data || []);
  };

  const saveKey = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    const res = await apiPut("/api/settings/ai/key", { openaiApiKey: newKey.trim() });
    setSaving(false);
    if (res.success) {
      setNewKey("");
      setShowKeyInput(false);
      loadSettings();
    } else {
      alert(res.message || "Failed to save key");
    }
  };

  const deleteKey = async () => {
    if (!window.confirm("Remove your OpenAI API key?")) return;
    const res = await apiDelete("/api/settings/ai/key");
    if (res.success) {
      setHasKey(false);
      setMaskedKey("");
    }
  };

  const saveElevenLabsKey = async () => {
    if (!newElevenLabsKey.trim()) return;
    setSavingElevenLabs(true);
    const res = await apiPut("/api/settings/ai/elevenlabs-key", { elevenlabsApiKey: newElevenLabsKey.trim() });
    setSavingElevenLabs(false);
    if (res.success) {
      setNewElevenLabsKey("");
      setShowElevenLabsInput(false);
      loadSettings();
    } else {
      alert(res.message || "Failed to save key");
    }
  };

  const deleteElevenLabsKey = async () => {
    if (!window.confirm("Remove your ElevenLabs API key?")) return;
    const res = await apiDelete("/api/settings/ai/elevenlabs-key");
    if (res.success) {
      setHasElevenLabsKey(false);
      setMaskedElevenLabsKey("");
    }
  };

  const saveOpenRouterKey = async () => {
    if (!newOpenRouterKey.trim()) return;
    setSavingOpenRouter(true);
    const res = await apiPut("/api/settings/ai/openrouter-key", { openrouterApiKey: newOpenRouterKey.trim() });
    setSavingOpenRouter(false);
    if (res.success) {
      setNewOpenRouterKey("");
      setShowOpenRouterInput(false);
      loadSettings();
    } else {
      alert(res.message || "Failed to save key");
    }
  };

  const deleteOpenRouterKey = async () => {
    if (!window.confirm("Remove your OpenRouter API key?")) return;
    const res = await apiDelete("/api/settings/ai/openrouter-key");
    if (res.success) {
      setHasOpenRouterKey(false);
      setMaskedOpenRouterKey("");
    }
  };

  const saveProvider = async (provider = aiProvider, model = aiModel) => {
    setSavingProvider(true);
    const res = await apiPut("/api/settings/ai/provider", { provider, model });
    setSavingProvider(false);
    if (res.success) loadSettings();
    else alert(res.message || "Failed to update provider");
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiPost("/api/settings/ai/test", {});
      setTestResult(res);
    } catch (err) {
      setTestResult({ ok: false, chat: { ok: false, message: err?.message || "Test failed." } });
    } finally {
      setTesting(false);
    }
  };

  const runTest = async (target) => {
    setTestState((s) => ({ ...s, [target]: { loading: true, result: null } }));
    try {
      const res = await apiPost("/api/settings/ai/test", { target });
      setTestState((s) => ({ ...s, [target]: { loading: false, result: res } }));
    } catch (err) {
      setTestState((s) => ({
        ...s,
        [target]: { loading: false, result: { ok: false, message: err?.message || "Test failed." } },
      }));
    }
  };

  const saveKb = async () => {
    if (!kbForm.name.trim() || !kbForm.content.trim()) return;
    setKbSaving(true);

    let res;
    if (editingKb) {
      res = await apiPut(`/api/settings/ai/knowledge/${editingKb.id || editingKb._id}`, kbForm);
    } else {
      res = await apiPost("/api/settings/ai/knowledge", kbForm);
    }

    setKbSaving(false);
    if (res.success) {
      setShowKbForm(false);
      setEditingKb(null);
      setKbForm({ name: "", type: "text", content: "" });
      loadKnowledgeBases();
    } else {
      alert(res.message || "Failed to save");
    }
  };

  const deleteKb = async (id) => {
    if (!window.confirm("Delete this knowledge base entry?")) return;
    const res = await apiDelete(`/api/settings/ai/knowledge/${id}`);
    if (res.success) loadKnowledgeBases();
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setKbForm((prev) => ({
        ...prev,
        type: "csv",
        content: ev.target.result,
        name: prev.name || file.name.replace(/\.[^.]+$/, ""),
      }));
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const scanSite = async () => {
    if (!siteUrl.trim()) return;
    setSiteScanning(true);
    setSiteError("");
    setSitePreview(null);
    try {
      const res = await apiPost("/api/settings/ai/knowledge/scrape", {
        url: siteUrl.trim(),
        name: siteName.trim() || undefined,
        maxPages: siteMaxPages,
      });
      if (res.success) setSitePreview(res);
      else setSiteError(res.message || "Could not scan that website.");
    } catch (err) {
      setSiteError(err?.message || "Could not scan that website.");
    } finally {
      setSiteScanning(false);
    }
  };

  const saveSitePreview = async () => {
    if (!sitePreview) return;
    setSiteSaving(true);
    const res = await apiPost("/api/settings/ai/knowledge", {
      name: sitePreview.name,
      type: "url",
      content: sitePreview.content,
    });
    setSiteSaving(false);
    if (res.success) {
      setSitePreview(null);
      setSiteUrl("");
      setSiteName("");
      loadKnowledgeBases();
    } else {
      alert(res.message || "Failed to save knowledge base");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* LEFT COLUMN: API integrations */}
      <div className="space-y-8">
      {/* AI Provider */}
      <div>
        <h3 className="text-lg font-semibold text-ink-primary flex items-center gap-2">
          <FaMagic className="text-fuchsia-500" /> AI Provider
        </h3>
        <p className="text-sm text-ink-muted mt-1">
          Which model provider powers your flows and knowledge base.
        </p>
        <div className="mt-4 p-4 rounded-xl border border-surface-border bg-surface-card space-y-3">
          <div className="flex gap-2">
            {[
              { key: "openai", label: "OpenAI" },
              { key: "openrouter", label: "OpenRouter" },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setAiProvider(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  aiProvider === p.key
                    ? "bg-fuchsia-500 text-white border-fuchsia-500"
                    : "border-surface-border text-ink-secondary hover:bg-surface-bg"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="input-group">
            <label className="input-label">Model <span className="text-ink-muted font-normal">(optional)</span></label>
            <input
              className="input"
              placeholder={aiProvider === "openrouter" ? "openai/gpt-4o-mini, anthropic/claude-3.5-sonnet" : "gpt-4o-mini"}
              value={aiModel}
              onChange={(e) => setAiModel(e.target.value)}
            />
            <p className="input-hint">
              {aiProvider === "openrouter"
                ? "Any OpenRouter model slug. Leave blank to use openai/gpt-4o-mini."
                : "Any OpenAI chat model. Leave blank to use gpt-4o-mini."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-primary btn-sm"
              onClick={() => saveProvider()}
              disabled={savingProvider}
            >
              {savingProvider ? "Saving..." : "Save provider"}
            </button>
            <button
              className="btn-sm border border-surface-border text-ink-secondary hover:bg-surface-bg"
              onClick={testConnection}
              disabled={testing}
            >
              {testing ? "Testing..." : "Test connection"}
            </button>
          </div>
          {testResult && (
            <div
              className={`rounded-lg border p-2.5 text-xs space-y-1 ${
                testResult.ok
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              <p className="font-medium">
                {testResult.ok ? "AI connection OK" : "AI connection problem"}
              </p>
              {testResult.chat && (
                <p>
                  <span className="font-medium">Chat:</span> {testResult.chat.message}
                  {testResult.chat.latencyMs ? ` (${testResult.chat.latencyMs} ms)` : ""}
                </p>
              )}
              {testResult.voice && testResult.voice.configured && (
                <p>
                  <span className="font-medium">Voice:</span> {testResult.voice.message}
                </p>
              )}
            </div>
          )}
          {aiProvider === "openrouter" && !hasOpenRouterKey && (
            <p className="text-2xs text-amber-600">Add your OpenRouter key below to use this provider.</p>
          )}
          {aiProvider === "openai" && !hasKey && (
            <p className="text-2xs text-amber-600">Add your OpenAI key below to use this provider.</p>
          )}
        </div>
      </div>

      {/* OpenAI API Key */}
      <div>
        <h3 className="text-lg font-semibold text-ink-primary flex items-center gap-2">
          <FaRobot className="text-indigo-500" /> OpenAI Integration
        </h3>
        <p className="text-sm text-ink-muted mt-1">
          Connect your OpenAI account to enable AI-powered responses in your flows.
        </p>

        <div className="mt-4 p-4 rounded-xl border border-surface-border bg-surface-card space-y-3">
          {hasKey && !showKeyInput ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-ink-primary font-medium">API Key configured</p>
                <p className="text-xs text-ink-muted font-mono">{maskedKey}</p>
              </div>
              <div className="flex gap-2 items-center">
                <TestKeyButton target="openai" testState={testState} onTest={runTest} />
                <button
                  className="btn-sm border border-surface-border text-ink-secondary hover:bg-surface-bg"
                  onClick={() => setShowKeyInput(true)}
                >
                  Change
                </button>
                <button className="btn-sm btn-danger" onClick={deleteKey}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="input-label">OpenAI API Key</label>
              <input
                type="password"
                className="input"
                placeholder="sk-..."
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
              <div className="flex gap-2 items-center">
                <button
                  className="btn-primary btn-sm"
                  onClick={saveKey}
                  disabled={saving || !newKey.trim()}
                >
                  {saving ? "Saving..." : "Save Key"}
                </button>
                {hasKey && <TestKeyButton target="openai" testState={testState} onTest={runTest} />}
                {hasKey && (
                  <button
                    className="btn-sm border border-surface-border text-ink-secondary"
                    onClick={() => {
                      setShowKeyInput(false);
                      setNewKey("");
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
              <p className="text-2xs text-ink-faint">
                Your key is stored securely and only used for your flow automations.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* OpenRouter API Key */}
      <div>
        <h3 className="text-lg font-semibold text-ink-primary flex items-center gap-2">
          <FaGlobe className="text-fuchsia-500" /> OpenRouter
        </h3>
        <p className="text-sm text-ink-muted mt-1">
          One key for hundreds of models — Claude, Gemini, Llama, GPT and more.
        </p>

        <div className="mt-4 p-4 rounded-xl border border-surface-border bg-surface-card space-y-3">
          {hasOpenRouterKey && !showOpenRouterInput ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-ink-primary font-medium">API Key configured</p>
                <p className="text-xs text-ink-muted font-mono">{maskedOpenRouterKey}</p>
              </div>
              <div className="flex gap-2 items-center">
                <TestKeyButton target="openrouter" testState={testState} onTest={runTest} />
                <button
                  className="btn-sm border border-surface-border text-ink-secondary hover:bg-surface-bg"
                  onClick={() => setShowOpenRouterInput(true)}
                >
                  Change
                </button>
                <button className="btn-sm btn-danger" onClick={deleteOpenRouterKey}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="input-label">OpenRouter API Key</label>
              <input
                type="password"
                className="input"
                placeholder="sk-or-..."
                value={newOpenRouterKey}
                onChange={(e) => setNewOpenRouterKey(e.target.value)}
              />
              <div className="flex gap-2 items-center">
                <button
                  className="btn-primary btn-sm"
                  onClick={saveOpenRouterKey}
                  disabled={savingOpenRouter || !newOpenRouterKey.trim()}
                >
                  {savingOpenRouter ? "Saving..." : "Save Key"}
                </button>
                {hasOpenRouterKey && <TestKeyButton target="openrouter" testState={testState} onTest={runTest} />}
                {hasOpenRouterKey && (
                  <button
                    className="btn-sm border border-surface-border text-ink-secondary"
                    onClick={() => {
                      setShowOpenRouterInput(false);
                      setNewOpenRouterKey("");
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
              <p className="text-2xs text-ink-faint">
                Get your key at{" "}
                <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" className="text-brand hover:underline">
                  openrouter.ai/keys
                </a>. Select OpenRouter as the provider above to use it.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ElevenLabs API Key */}
      <div>
        <h3 className="text-lg font-semibold text-ink-primary flex items-center gap-2">
          <FaMicrophone className="text-pink-500" /> ElevenLabs Voice AI
        </h3>
        <p className="text-sm text-ink-muted mt-1">
          Connect ElevenLabs to send AI-generated voice notes in your flows.
        </p>

        <div className="mt-4 p-4 rounded-xl border border-surface-border bg-surface-card space-y-3">
          {hasElevenLabsKey && !showElevenLabsInput ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-ink-primary font-medium">API Key configured</p>
                <p className="text-xs text-ink-muted font-mono">{maskedElevenLabsKey}</p>
              </div>
              <div className="flex gap-2 items-center">
                <TestKeyButton target="elevenlabs" testState={testState} onTest={runTest} />
                <button
                  className="btn-sm border border-surface-border text-ink-secondary hover:bg-surface-bg"
                  onClick={() => setShowElevenLabsInput(true)}
                >
                  Change
                </button>
                <button className="btn-sm btn-danger" onClick={deleteElevenLabsKey}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="input-label">ElevenLabs API Key</label>
              <input
                type="password"
                className="input"
                placeholder="xi-..."
                value={newElevenLabsKey}
                onChange={(e) => setNewElevenLabsKey(e.target.value)}
              />
              <div className="flex gap-2 items-center">
                <button
                  className="btn-primary btn-sm"
                  onClick={saveElevenLabsKey}
                  disabled={savingElevenLabs || !newElevenLabsKey.trim()}
                >
                  {savingElevenLabs ? "Saving..." : "Save Key"}
                </button>
                {hasElevenLabsKey && <TestKeyButton target="elevenlabs" testState={testState} onTest={runTest} />}
                {hasElevenLabsKey && (
                  <button
                    className="btn-sm border border-surface-border text-ink-secondary"
                    onClick={() => {
                      setShowElevenLabsInput(false);
                      setNewElevenLabsKey("");
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
              <p className="text-2xs text-ink-faint">
                Get your key at{" "}
                <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer" className="text-brand hover:underline">
                  elevenlabs.io/app/settings/api-keys
                </a>. Used for Voice Note nodes in flows.
              </p>
            </div>
          )}
        </div>
      </div>

      </div>
      {/* RIGHT COLUMN: Tips & Knowledge Base */}
      <div className="space-y-8">
      {/* Tips & Status */}
      <div>
        <h3 className="text-lg font-semibold text-ink-primary">Tips & Status</h3>
        <p className="text-sm text-ink-muted mt-1">
          Quick reference for connecting your AI providers.
        </p>
        <div className="mt-4 p-4 rounded-xl border border-surface-border bg-surface-card space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-ink-secondary">OpenAI</span>
            {hasKey ? (
              <span className="badge-success text-2xs">Connected</span>
            ) : (
              <span className="badge-warning text-2xs">Not configured</span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-ink-secondary">ElevenLabs</span>
            {hasElevenLabsKey ? (
              <span className="badge-success text-2xs">Connected</span>
            ) : (
              <span className="badge-warning text-2xs">Not configured</span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-ink-secondary">Knowledge entries</span>
            <span className="text-ink-primary font-medium">{knowledgeBases.length}</span>
          </div>
          <div className="pt-3 border-t border-surface-border space-y-2 text-xs text-ink-muted">
            <p>
              <span className="text-ink-secondary font-medium">Where to find keys:</span>
            </p>
            <ul className="space-y-1 list-disc pl-4">
              <li>
                OpenAI:{" "}
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-brand hover:underline">
                  platform.openai.com/api-keys
                </a>
              </li>
              <li>
                ElevenLabs:{" "}
                <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer" className="text-brand hover:underline">
                  elevenlabs.io/app/settings/api-keys
                </a>
              </li>
            </ul>
            <p className="pt-1 text-2xs text-ink-faint">
              Keys are encrypted at rest and only used to run your flows.
            </p>
          </div>
        </div>
      </div>

      {/* Knowledge Base */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-ink-primary">Knowledge Base</h3>
            <p className="text-sm text-ink-muted mt-0.5">
              Train your AI with custom knowledge. Paste text or upload CSV data.
            </p>
          </div>
          <button
            className="btn-primary btn-sm flex items-center gap-1.5"
            onClick={() => {
              setEditingKb(null);
              setKbForm({ name: "", type: "text", content: "" });
              setShowKbForm(true);
            }}
          >
            <FaPlus size={10} /> Add
          </button>
        </div>

        {/* Website → Q&A builder */}
        <div className="mt-4 p-4 rounded-xl border border-fuchsia-200 bg-fuchsia-50/40 space-y-3">
          <div className="flex items-center gap-2">
            <FaGlobe className="text-fuchsia-500" size={13} />
            <p className="text-sm font-medium text-ink-primary">Build from your website</p>
          </div>
          <p className="text-xs text-ink-muted">
            We read your sitemap and crawl up to 100 pages, then OpenAI writes question &amp; answer
            pairs grounded in your site. Review them, then save.
          </p>

          <div className="input-group">
            <input
              className="input"
              placeholder="https://yourbusiness.com  (or a sitemap.xml URL)"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              disabled={siteScanning}
            />
          </div>

          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="Brand name (optional)"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              disabled={siteScanning}
            />
            <input
              className="input w-24"
              type="number"
              min="1"
              max="100"
              title="Max pages"
              value={siteMaxPages}
              onChange={(e) => setSiteMaxPages(Number(e.target.value))}
              disabled={siteScanning}
            />
          </div>

          <button
            className="btn-primary btn-sm flex items-center gap-1.5"
            onClick={scanSite}
            disabled={siteScanning || !siteUrl.trim() || !hasKey}
          >
            <FaMagic size={10} />
            {siteScanning ? "Scanning site…" : "Scan & build Q&A"}
          </button>
          {!hasKey && (
            <p className="text-2xs text-ink-faint">Add an OpenAI API key above first.</p>
          )}
          {siteError && <p className="text-xs text-red-600">{siteError}</p>}

          {sitePreview && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="badge-info text-2xs">
                  {sitePreview.qa?.length || 0} Q&amp;A
                </span>
                <span className="badge-info text-2xs">{sitePreview.pages} pages</span>
                <span className="text-2xs text-ink-faint truncate">{sitePreview.sourceUrl}</span>
              </div>
              <pre className="max-h-48 overflow-y-auto rounded-lg border border-surface-border bg-surface-card p-3 text-2xs font-mono whitespace-pre-wrap">
                {sitePreview.content}
              </pre>
              <div className="flex gap-2">
                <button
                  className="btn-primary btn-sm flex items-center gap-1.5"
                  onClick={saveSitePreview}
                  disabled={siteSaving}
                >
                  <FaSave size={10} />
                  {siteSaving ? "Saving…" : "Save as knowledge base"}
                </button>
                <button
                  className="btn-sm border border-surface-border text-ink-secondary"
                  onClick={() => setSitePreview(null)}
                  disabled={siteSaving}
                >
                  Discard
                </button>
              </div>
            </div>
          )}
        </div>

        {/* KB Form */}
        {showKbForm && (
          <div className="mt-4 p-4 rounded-xl border border-indigo-200 bg-indigo-50/30 space-y-3">
            <div className="input-group">
              <label className="input-label">Name</label>
              <input
                className="input"
                placeholder="e.g. Product FAQ, Pricing Info"
                value={kbForm.name}
                onChange={(e) => setKbForm({ ...kbForm, name: e.target.value })}
              />
            </div>

            <div className="input-group">
              <label className="input-label">Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                    kbForm.type === "text"
                      ? "bg-indigo-500 text-white border-indigo-500"
                      : "border-surface-border text-ink-secondary"
                  }`}
                  onClick={() => setKbForm({ ...kbForm, type: "text" })}
                >
                  Text
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                    kbForm.type === "csv"
                      ? "bg-indigo-500 text-white border-indigo-500"
                      : "border-surface-border text-ink-secondary"
                  }`}
                  onClick={() => setKbForm({ ...kbForm, type: "csv" })}
                >
                  CSV
                </button>
              </div>
            </div>

            {kbForm.type === "csv" && (
              <div className="input-group">
                <label className="mt-1 block">
                  <input
                    type="file"
                    accept=".csv,.txt"
                    className="hidden"
                    onChange={handleCsvUpload}
                  />
                  <span className="text-xs text-brand cursor-pointer hover:underline">
                    Upload CSV file
                  </span>
                </label>
              </div>
            )}

            <div className="input-group">
              <label className="input-label">Content</label>
              <textarea
                className="input min-h-[150px] font-mono text-xs"
                placeholder={
                  kbForm.type === "csv"
                    ? "CSV data will appear here after upload, or paste directly..."
                    : "Paste your knowledge base content here...\n\nExample:\nQ: What are your business hours?\nA: We are open Monday-Friday 9am-5pm.\n\nQ: What is your return policy?\nA: We offer 30-day returns on all products."
                }
                value={kbForm.content}
                onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })}
              />
              <p className="input-hint">{(kbForm.content || "").length} characters</p>
            </div>

            <div className="flex gap-2">
              <button
                className="btn-primary btn-sm flex items-center gap-1.5"
                onClick={saveKb}
                disabled={kbSaving || !kbForm.name.trim() || !kbForm.content.trim()}
              >
                <FaSave size={10} />
                {kbSaving ? "Saving..." : editingKb ? "Update" : "Save"}
              </button>
              <button
                className="btn-sm border border-surface-border text-ink-secondary"
                onClick={() => {
                  setShowKbForm(false);
                  setEditingKb(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* KB List */}
        <div className="mt-4 space-y-2">
          {knowledgeBases.length === 0 && !showKbForm && (
            <p className="text-sm text-ink-muted text-center py-6">
              No knowledge base entries yet. Add one to train your AI.
            </p>
          )}
          {knowledgeBases.map((kb) => (
            <div
              key={kb.id || kb._id}
              className="flex items-center justify-between p-3 rounded-lg border border-surface-border bg-surface-card"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-ink-primary truncate">{kb.name}</p>
                  <span className="badge-info text-2xs">{kb.type}</span>
                  {!kb.active && <span className="badge-warning text-2xs">Disabled</span>}
                </div>
                <p className="text-2xs text-ink-muted mt-0.5">
                  {(kb.content || "").length} chars — updated {new Date(kb.updatedAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  className="p-1.5 text-ink-muted hover:text-ink-primary"
                  title="Edit"
                  onClick={() => {
                    setEditingKb(kb);
                    setKbForm({ name: kb.name, type: kb.type, content: kb.content });
                    setShowKbForm(true);
                  }}
                >
                  <FaEdit size={12} />
                </button>
                <button
                  className="p-1.5 text-ink-muted hover:text-red-500"
                  title="Delete"
                  onClick={() => deleteKb(kb.id || kb._id)}
                >
                  <FaTrash size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
